import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Plus, Send, Trash2, Loader2, Mic, Square } from "lucide-react";
import { useRecorder } from "@/lib/recorder";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, API, errMsg } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = ["Come faccio l'ISEE per mia madre?", "Mi è arrivata una cartella esattoriale, cosa faccio?", "Quali documenti servono per la Legge 104?", "Conviene fare il 730 da solo?"];

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${m.role === "user" ? "bg-[#1E3A8A] text-white rounded-br-md" : "bg-white border rounded-bl-md md"}`}>
      {m.role === "user" ? m.content : <><ReactMarkdown components={{ a: ({ node, ...p }) => <a {...p} target="_blank" rel="noopener noreferrer" /> }}>{m.content || "…"}</ReactMarkdown>{m.model && <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">{m.model === "gpt" ? "ChatGPT" : "Claude"}</p>}</>}
    </div>
  </div>
);

async function streamReply(sid, text, model, onDelta) {
  const res = await fetch(`${API}/chat/sessions/${sid}/stream`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: text, model }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || "Errore");
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const ev = JSON.parse(p.slice(6));
      if (ev.delta) onDelta(ev.delta);
      if (ev.error) throw new Error(ev.error);
    }
  }
}

export default function Chat() {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [model, setModel] = useState(() => localStorage.getItem("bz_chat_model") || "claude");
  const pickModel = (m) => {
    setModel(m);
    localStorage.setItem("bz_chat_model", m);
  };
  const end = useRef();
  const { recording, start, stop } = useRecorder();
  const [transcribing, setTranscribing] = useState(false);
  const mic = async () => {
    if (!recording) {
      try {
        await start();
      } catch {
        toast.error("Permetti l'uso del microfono");
      }
      return;
    }
    const file = await stop();
    const fd = new FormData();
    fd.append("file", file);
    setTranscribing(true);
    try {
      const { data } = await api.post("/voice/transcribe", fd);
      setText((t) => (t ? t + " " : "") + data.text);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTranscribing(false);
    }
  };

  const loadSessions = useCallback(() => api.get("/chat/sessions").then(({ data }) => setSessions(data)), []);
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const open = async (id) => {
    setSid(id);
    const { data } = await api.get(`/chat/sessions/${id}/messages`);
    setMsgs(data);
  };
  const newChat = () => {
    setSid(null);
    setMsgs([]);
  };
  const remove = async (id) => {
    await api.delete(`/chat/sessions/${id}`);
    if (id === sid) newChat();
    loadSessions();
  };

  const send = async (q) => {
    const msg = (q ?? text).trim();
    if (!msg || busy) return;
    setText("");
    setBusy(true);
    let id = sid;
    try {
      if (!id) {
        const { data } = await api.post("/chat/sessions");
        id = data.session_id;
        setSid(id);
      }
      setMsgs((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", model }]);
      await streamReply(id, msg, model, (d) => setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], content: m[m.length - 1].content + d }]));
      loadSessions();
    } catch (e) {
      toast.error(e.message || errMsg(e));
      setMsgs((m) => (m[m.length - 1]?.content === "" ? m.slice(0, -1) : m));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-4 gap-6 h-[calc(100vh-8rem)]" data-testid="chat-page">
      <div className="hidden lg:flex flex-col rounded-3xl border bg-white p-4 overflow-hidden">
        <Button onClick={newChat} className="rounded-full" data-testid="chat-new-btn"><Plus className="h-4 w-4 mr-2" /> Nuova chat</Button>
        <div className="mt-4 overflow-auto space-y-1">
          {sessions.map((s) => (
            <div key={s.session_id} className={`group flex items-center rounded-xl ${s.session_id === sid ? "bg-slate-100" : "hover:bg-slate-50"}`}>
              <button onClick={() => open(s.session_id)} className="flex-1 text-left px-3 py-2 text-sm truncate" data-testid={`chat-session-${s.session_id}`}>{s.title}</button>
              <button onClick={() => remove(s.session_id)} className="p-2 opacity-0 group-hover:opacity-100 text-red-600" data-testid={`chat-delete-${s.session_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-3 flex flex-col rounded-3xl border bg-[#F1F5F9]/60 overflow-hidden">
        <div className="flex-1 overflow-auto p-4 md:p-8 space-y-4" data-testid="chat-messages">
          {msgs.length === 0 && (
            <div className="max-w-xl">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E07A5F]">Chiedi a Burocrazy</p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight">Che pratica ti preoccupa oggi?</h1>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {SUGGESTIONS.map((s, i) => <button key={s} onClick={() => send(s)} className="lift text-left rounded-2xl border bg-white p-4 text-sm" data-testid={`chat-suggestion-${i}`}>{s}</button>)}
              </div>
            </div>
          )}
          {msgs.map((m, i) => <Bubble key={i} m={m} />)}
          {busy && msgs[msgs.length - 1]?.content === "" && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          <div ref={end} />
        </div>
        <div className="border-t bg-white px-3 md:px-4 pt-3 flex items-center gap-2 text-xs text-slate-500">
          Risponde:
          {[["claude", "Claude"], ["gpt", "ChatGPT"]].map(([v, l]) => (
            <button key={v} onClick={() => pickModel(v)} data-testid={`chat-model-${v}`}
              className={`rounded-full px-3 py-1 border transition-colors ${model === v ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <div className="bg-white p-3 md:p-4 flex gap-2 items-end">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder="Scrivi la tua domanda…" className="resize-none min-h-[48px]"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="chat-input" />
          <Button onClick={mic} variant="outline" disabled={transcribing} className={`rounded-full h-12 w-12 p-0 ${recording ? "bg-red-600 text-white hover:bg-red-700 hover:text-white" : ""}`} data-testid="chat-mic-btn">{transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}</Button>
          <Button onClick={() => send()} disabled={busy || !text.trim()} className="rounded-full h-12 w-12 p-0" data-testid="chat-send-btn"><Send className="h-4 w-4" /></Button>
        </div>
        <p className="px-4 pb-3 bg-white text-[11px] text-slate-400">Risposte orientative generate da AI: verifica sempre sui portali ufficiali.</p>
      </div>
    </div>
  );
}
